// Client runtime entry — the only script shipped to the browser. Registers the
// Web Components and progressively enhances the chrome and a few interactive bits.
// No framework; reactive core only.

import { defineComponent } from "./component";
import { rotatorDef } from "../components/rotator";
import { fmtEuro, fmtPct } from "../lib/chartmath";

defineComponent(rotatorDef);

const doc = document;
const animated = doc.documentElement.classList.contains("anim");

// --- Reveal on view ---------------------------------------------------------
if (animated) {
  for (const el of doc.querySelectorAll<HTMLElement>("[data-reveal-words]")) splitWords(el);
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add("is-in");
        io.unobserve(e.target);
      }
    },
    { rootMargin: "0px 0px -10% 0px", threshold: 0.12 },
  );
  for (const el of doc.querySelectorAll(".reveal")) io.observe(el);
}
function splitWords(el: HTMLElement): void {
  const out: Node[] = [];
  let i = 0;
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      for (const tok of (node.textContent ?? "").split(/(\s+)/)) {
        if (tok === "") continue;
        if (/^\s+$/.test(tok)) out.push(doc.createTextNode(tok));
        else out.push(wordSpan(tok, i++));
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const span = wordSpan("", i++);
      span.appendChild(node.cloneNode(true));
      out.push(span);
    }
  }
  el.replaceChildren(...out);
}
function wordSpan(text: string, i: number): HTMLElement {
  const span = doc.createElement("span");
  span.className = "w-rev";
  span.style.setProperty("--i", String(i));
  if (text) span.textContent = text;
  return span;
}

// --- Hero photo fade --------------------------------------------------------
const heroBg = doc.querySelector(".hero__bg");
const heroImg = heroBg?.querySelector("img") as HTMLImageElement | null;
if (heroBg && heroImg) {
  const show = () => heroBg.classList.add("is-loaded");
  if (heroImg.complete) show();
  else {
    heroImg.addEventListener("load", show, { once: true });
    heroImg.addEventListener("error", show, { once: true });
  }
}

// --- Header scroll state ----------------------------------------------------
const header = doc.querySelector(".site-header");
if (header) {
  const onScroll = () => header.classList.toggle("is-scrolled", window.scrollY > 8);
  onScroll();
  addEventListener("scroll", onScroll, { passive: true });
}

// --- Mega-menu (hover-intent + click / keyboard / touch) --------------------
// CSS already opens on :hover with a bridge pseudo-element; JS adds a close
// delay so brief diagonal cursor travel never dismisses the panel.
const megaItems = Array.from(doc.querySelectorAll<HTMLElement>(".mainnav__item--mega"));
const canHover = typeof matchMedia === "function" && matchMedia("(hover: hover)").matches;
let megaTimer: ReturnType<typeof setTimeout> | undefined;
function setMega(it: Element, open: boolean): void {
  it.querySelector(".megamenu")?.classList.toggle("is-open", open);
  it.querySelector(".mainnav__link")?.setAttribute("aria-expanded", String(open));
}
function closeMega(except?: Element): void {
  for (const it of megaItems) if (it !== except) setMega(it, false);
}
for (const it of megaItems) {
  const trigger = it.querySelector<HTMLButtonElement>(".mainnav__link");
  const panel = it.querySelector<HTMLElement>(".megamenu");
  if (!trigger || !panel) continue;
  trigger.addEventListener("click", (e) => {
    e.preventDefault();
    const open = !panel.classList.contains("is-open");
    closeMega(it);
    setMega(it, open);
  });
  if (canHover) {
    it.addEventListener("pointerenter", () => { clearTimeout(megaTimer); closeMega(it); setMega(it, true); });
    it.addEventListener("pointerleave", () => { megaTimer = setTimeout(() => setMega(it, false), 200); });
  }
}
doc.addEventListener("keydown", (e) => {
  if ((e as KeyboardEvent).key === "Escape") closeMega();
});
doc.addEventListener("click", (e) => {
  if (!(e.target as HTMLElement).closest?.(".mainnav__item--mega")) closeMega();
});

// --- Mobile nav -------------------------------------------------------------
const navToggle = doc.querySelector(".nav-toggle");
const mobilenav = doc.querySelector(".mobilenav");
if (navToggle && mobilenav) {
  navToggle.addEventListener("click", () => {
    const open = mobilenav.classList.toggle("is-open");
    navToggle.setAttribute("aria-expanded", String(open));
  });
}

// --- Tabs -------------------------------------------------------------------
for (const group of doc.querySelectorAll<HTMLElement>("[data-tabs]")) {
  const tabsEls = Array.from(group.querySelectorAll<HTMLElement>(".tabs__tab"));
  const panels = Array.from(group.querySelectorAll<HTMLElement>(".tabs__panel"));
  tabsEls.forEach((tab, idx) => {
    tab.addEventListener("click", () => {
      tabsEls.forEach((t) => t.setAttribute("aria-selected", "false"));
      panels.forEach((p) => (p.hidden = true));
      tab.setAttribute("aria-selected", "true");
      if (panels[idx]) panels[idx].hidden = false;
    });
  });
}

// --- Onboarding chips + goal limit ------------------------------------------
const painsField = doc.querySelector<HTMLTextAreaElement>("#ob-pains");
for (const chip of doc.querySelectorAll<HTMLButtonElement>("[data-chip]")) {
  chip.addEventListener("click", () => {
    if (!painsField) return;
    const t = chip.dataset.chip ?? "";
    painsField.value = painsField.value ? `${painsField.value}, ${t}` : t;
    painsField.focus();
  });
}
for (const grp of doc.querySelectorAll<HTMLElement>(".opts[data-max]")) {
  const max = parseInt(grp.dataset.max ?? "99", 10);
  const boxes = Array.from(grp.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
  grp.addEventListener("change", () => {
    const checked = boxes.filter((b) => b.checked);
    if (checked.length > max) {
      boxes.forEach((b) => { if (!checked.slice(0, max).includes(b)) b.checked = false; });
    }
  });
}

// --- Help-center search filter ----------------------------------------------
const helpSearch = doc.querySelector<HTMLInputElement>("[data-help-search]");
const helpFaq = doc.querySelector<HTMLElement>("[data-help-faq]");
const helpEmpty = doc.querySelector<HTMLElement>("[data-help-empty]");
if (helpSearch && helpFaq) {
  const items = Array.from(helpFaq.querySelectorAll<HTMLElement>(".faq__item"));
  const groups = Array.from(helpFaq.querySelectorAll<HTMLElement>(".faq"));
  helpSearch.addEventListener("input", () => {
    const q = helpSearch.value.trim().toLowerCase();
    let any = false;
    for (const it of items) {
      const match = !q || (it.textContent ?? "").toLowerCase().includes(q);
      it.hidden = !match;
      if (match) any = true;
    }
    for (const g of groups) {
      const visible = Array.from(g.querySelectorAll<HTMLElement>(".faq__item")).some((i) => !i.hidden);
      g.style.display = visible ? "" : "none";
    }
    if (helpEmpty) helpEmpty.hidden = any;
  });
}

// --- Hebel-Rechner ----------------------------------------------------------
const calc = doc.querySelector<HTMLFormElement>("[data-calc]");
if (calc) {
  const out = calc.querySelector<HTMLElement>("[data-calc-out]");
  const fmt = (n: number) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
  const run = () => {
    if (!out) return;
    const chairs = Number((calc.elements.namedItem("chairs") as HTMLInputElement)?.value || 0);
    const rev = Number((calc.elements.namedItem("rev") as HTMLInputElement)?.value || 0);
    const noshow = Number((calc.elements.namedItem("noshow") as HTMLInputElement)?.value || 0);
    const monthly = chairs * rev;
    const noshowSave = monthly * (noshow / 100) * 0.5;
    const followLift = monthly * 0.05;
    out.innerHTML = `
      <div class="calc__line"><span>No-Show-Schutz, mögliche Ersparnis</span><b class="mono">${fmt(noshowSave)} / Monat</b></div>
      <div class="calc__line"><span>Folgetermine, möglicher Mehrumsatz</span><b class="mono">${fmt(followLift)} / Monat</b></div>
      <div class="calc__line"><span>Zusammen, projiziert</span><b class="mono">${fmt(noshowSave + followLift)} / Monat</b></div>`;
  };
  calc.addEventListener("input", run);
  run();
}

// --- Regler-Rechner (Beispielrechnung) --------------------------------------
// Ergebnis = Produkt aller Slider-Werte, als Euro. Aktualisiert die Anzeige je Regler.
for (const form of doc.querySelectorAll<HTMLFormElement>("[data-fcalc]")) {
  const inputs = Array.from(form.querySelectorAll<HTMLInputElement>("[data-fc-input]"));
  const result = form.querySelector<HTMLElement>("[data-fc-result]");
  const euro = (n: number) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
  const run = () => {
    let product = 1;
    for (const inp of inputs) {
      const v = Number(inp.value) || 0;
      product *= inp.dataset.fcPercent !== undefined ? v / 100 : v;
      const out = form.querySelector<HTMLElement>(`[data-fc-out="${inp.id}"]`);
      if (out) {
        const dec = Number(inp.step) < 1 ? 1 : 0;
        out.textContent = v.toLocaleString("de-DE", { minimumFractionDigits: dec, maximumFractionDigits: dec }) + (inp.dataset.fcSuffix || "");
      }
    }
    if (result) result.textContent = euro(product);
  };
  form.addEventListener("input", run);
  run();
}

// --- Onboarding wizard (stepper) --------------------------------------------
const obForm = doc.querySelector<HTMLFormElement>("[data-ob]");
const obStepsNav = doc.querySelector<HTMLElement>("[data-ob-steps]");
const obSubmit = doc.querySelector<HTMLElement>("[data-ob-nav]");
if (obForm && obStepsNav && obSubmit) {
  const steps = Array.from(obForm.querySelectorAll<HTMLElement>(".ob__step"));
  if (steps.length > 1) {
    let cur = 0;
    let maxReached = 0;
    const dots: HTMLElement[] = [];

    const nav = doc.createElement("div");
    nav.className = "ob__navbtns";
    const back = doc.createElement("button");
    back.type = "button";
    back.className = "btn btn--secondary";
    back.textContent = "Zurück";
    const next = doc.createElement("button");
    next.type = "button";
    next.className = "btn btn--primary";
    next.textContent = "Weiter";
    nav.appendChild(back);
    nav.appendChild(next);
    obSubmit.parentNode?.insertBefore(nav, obSubmit);

    const isValid = (i: number): boolean => {
      const step = steps[i];
      if (!step) return true;
      for (const node of Array.from(step.querySelectorAll("input, select, textarea"))) {
        const el = node as HTMLInputElement;
        if (!el.checkValidity()) {
          el.reportValidity();
          return false;
        }
      }
      return true;
    };
    const show = (i: number): void => {
      cur = Math.max(0, Math.min(steps.length - 1, i));
      maxReached = Math.max(maxReached, cur);
      const last = steps.length - 1;
      steps.forEach((s, j) => { s.hidden = j !== cur; });
      dots.forEach((d, j) => {
        d.classList.toggle("is-active", j === cur);
        d.classList.toggle("is-done", j < cur);
        d.classList.toggle("is-reached", j <= maxReached);
      });
      back.style.visibility = cur === 0 ? "hidden" : "visible";
      next.hidden = cur === last;
      obSubmit.hidden = cur !== last;
    };

    steps.forEach((s, i) => {
      const label = s.getAttribute("data-label") ?? `Schritt ${i + 1}`;
      const li = doc.createElement("li");
      li.className = "ob-steps__i";
      li.innerHTML = `<span class="ob-steps__n">${String(i + 1).padStart(2, "0")}</span><span class="ob-steps__l">${label}</span>`;
      li.addEventListener("click", () => { if (i <= maxReached) show(i); });
      obStepsNav.appendChild(li);
      dots.push(li);
    });
    back.addEventListener("click", () => show(cur - 1));
    next.addEventListener("click", () => { if (isValid(cur)) show(cur + 1); });
    show(0);
  }
}

// --- Sam chat (onboarding) --------------------------------------------------
const samPanel = doc.querySelector<HTMLElement>("[data-sam]");
if (samPanel) {
  const samLog = samPanel.querySelector<HTMLElement>("[data-sam-log]");
  const samForm = samPanel.querySelector<HTMLFormElement>("[data-sam-form]");
  const samInput = samPanel.querySelector<HTMLInputElement>("[data-sam-input]");
  const samSuggest = samPanel.querySelector<HTMLElement>("[data-sam-suggest]");
  const samHistory: { role: string; content: string }[] = [];
  let samBusy = false;

  const bubble = (role: string, text: string): HTMLElement | null => {
    if (!samLog) return null;
    const div = doc.createElement("div");
    div.className = `sam-msg sam-msg--${role}`;
    div.textContent = text;
    samLog.appendChild(div);
    samLog.scrollTop = samLog.scrollHeight;
    return div;
  };
  const ask = async (q: string): Promise<void> => {
    const text = q.trim();
    if (!text || samBusy) return;
    samBusy = true;
    bubble("user", text);
    samHistory.push({ role: "user", content: text });
    if (samInput) samInput.value = "";
    if (samSuggest) samSuggest.innerHTML = "";
    const typing = bubble("sam", "Sam denkt nach …");
    typing?.classList.add("is-typing");
    try {
      const res = await fetch("/api/sam", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ history: samHistory, locale: document.documentElement.lang === "en" ? "en" : "de" }) });
      const data = (await res.json()) as { reply?: string; suggest?: string[] };
      typing?.remove();
      const reply = data.reply ?? "Entschuldige, das hat gerade nicht geklappt. Versuch es gleich noch einmal oder schreib uns über das Kontaktformular.";
      bubble("sam", reply);
      samHistory.push({ role: "sam", content: reply });
      if (samSuggest && Array.isArray(data.suggest) && data.suggest.length) {
        samSuggest.innerHTML = data.suggest.slice(0, 3).map((s) => `<button type="button" class="chip" data-sam-q="${s.replace(/"/g, "&quot;")}">${s}</button>`).join("");
        wireSuggest();
      }
    } catch {
      if (typing) {
        typing.classList.remove("is-typing");
        typing.textContent = "Verbindung unterbrochen. Versuch es gleich noch einmal.";
      }
    } finally {
      samBusy = false;
    }
  };
  function wireSuggest(): void {
    if (!samSuggest) return;
    for (const chip of samSuggest.querySelectorAll<HTMLElement>("[data-sam-q]")) {
      chip.addEventListener("click", (e) => {
        e.preventDefault();
        void ask(chip.getAttribute("data-sam-q") ?? chip.textContent ?? "");
      });
    }
  }
  wireSuggest();
  samForm?.addEventListener("submit", (e) => { e.preventDefault(); void ask(samInput?.value ?? ""); });
  bubble("sam", "Hey, ich bin Sam. Frag mich alles über Saloncare, oder fang einfach links mit dem Assistenten an. Ich helfe dir, den passenden Weg zu finden.");
}

// --- Testimonial carousel ---------------------------------------------------
// Endless auto-advance with a seamless wrap (the track is rendered twice), plus
// arrow-key, button and swipe control. Pauses on hover/focus/interaction.
for (const car of Array.from(doc.querySelectorAll<HTMLElement>("[data-carousel]"))) {
  const vp = car.querySelector<HTMLElement>("[data-carousel-vp]");
  const track = car.querySelector<HTMLElement>("[data-carousel-track]");
  if (!vp || !track) continue;
  const prev = car.querySelector<HTMLButtonElement>("[data-carousel-prev]");
  const nextBtn = car.querySelector<HTMLButtonElement>("[data-carousel-next]");
  const reduce = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  const step = (): number => {
    const card = track.querySelector<HTMLElement>(".tcard");
    const gap = parseFloat(getComputedStyle(track).columnGap || "24") || 24;
    return card ? card.getBoundingClientRect().width + gap : 340;
  };
  const half = (): number => track.scrollWidth / 2;
  const wrap = (): void => {
    const h = half();
    if (h <= 0) return;
    if (vp.scrollLeft >= h) vp.scrollLeft = vp.scrollLeft - h;
    else if (vp.scrollLeft <= 0) vp.scrollLeft = vp.scrollLeft + h;
  };
  const advance = (dir: number): void => {
    vp.scrollBy({ left: dir * step(), behavior: reduce ? "auto" : "smooth" });
  };

  nextBtn?.addEventListener("click", () => advance(1));
  prev?.addEventListener("click", () => advance(-1));
  vp.addEventListener("keydown", (e) => {
    const k = (e as KeyboardEvent).key;
    if (k === "ArrowRight") { e.preventDefault(); advance(1); }
    else if (k === "ArrowLeft") { e.preventDefault(); advance(-1); }
  });
  let settle: ReturnType<typeof setTimeout> | undefined;
  vp.addEventListener("scroll", () => { clearTimeout(settle); settle = setTimeout(wrap, 130); }, { passive: true });

  if (!reduce) {
    let paused = false;
    let nudge: ReturnType<typeof setTimeout> | undefined;
    car.addEventListener("pointerenter", () => { paused = true; });
    car.addEventListener("pointerleave", () => { paused = false; });
    car.addEventListener("focusin", () => { paused = true; });
    car.addEventListener("focusout", () => { paused = false; });
    vp.addEventListener("pointerdown", () => { paused = true; clearTimeout(nudge); nudge = setTimeout(() => { paused = false; }, 5000); });
    setInterval(() => { if (!paused && document.visibilityState === "visible") advance(1); }, 3800);
  }
}

// --- Investor-Deck: Beteiligungsrechner (Wandeldarlehen am Cap) ---------------
interface DeckScenario { key: string; label: string; exitVal: number; }
const deckDataEl = doc.querySelector<HTMLScriptElement>("[data-deck-data]");
if (deckDataEl) {
  const deck = JSON.parse(deckDataEl.textContent ?? "{}") as { cap: number; scenarios: DeckScenario[] };
  let amount = 50000;
  const fmtMult = (m: number): string => m.toLocaleString("de-DE", { maximumFractionDigits: 1 }) + "×";
  const setText = (sel: string, v: string): void => {
    for (const el of doc.querySelectorAll(sel)) el.textContent = v;
  };

  const update = (): void => {
    const own = amount / deck.cap;
    setText('[data-iv="amount"]', fmtEuro(amount));
    setText('[data-iv="now"]', fmtPct(own * 100));
    for (const c of deck.scenarios) {
      const val = own * c.exitVal;
      setText(`[data-iv-all="${c.key}"]`, `${fmtEuro(val)} · ${fmtMult(c.exitVal / deck.cap)}`);
    }
    for (const b of doc.querySelectorAll<HTMLButtonElement>("[data-iv-preset]")) {
      b.classList.toggle("is-active", Number(b.dataset.ivPreset) === amount);
    }
  };

  const range = doc.querySelector<HTMLInputElement>("[data-iv-range]");
  range?.addEventListener("input", () => {
    amount = Number(range.value) || amount;
    update();
  });
  for (const b of doc.querySelectorAll<HTMLButtonElement>("[data-iv-preset]")) {
    b.addEventListener("click", () => {
      amount = Number(b.dataset.ivPreset) || amount;
      if (range) range.value = String(amount);
      update();
    });
  }
  update();
}

// --- Native client-error capture --------------------------------------------
function report(kind: string, detail: unknown): void {
  try {
    const body = JSON.stringify({ kind, detail: String(detail), url: location.pathname, ts: Date.now() });
    navigator.sendBeacon?.("/api/client-error", body);
  } catch {
    /* ignore */
  }
}
window.addEventListener("error", (e: ErrorEvent) => report("error", e.message));
window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => report("unhandledrejection", e.reason));
